import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import type { SecretMetaResponse } from "@sss/shared";
import {
  ApiError,
  downloadSecret,
  getSecretMeta,
  reportBrowserEvent,
  requestOtp,
  verifyOtp,
} from "../lib/api";
import { decryptFileStream, decryptMeta, decryptText } from "../lib/crypto";
import { linkKey } from "../lib/linkKey";
import { storeReplyGrant } from "../lib/replyGrant";
import { useI18n } from "../i18n";
import { Card } from "../components/Card";
import { CopyButton } from "../components/CopyButton";

type Stage =
  | { name: "loading" }
  | { name: "invalid_link" }
  | { name: "not_found" }
  | { name: "already_retrieved" }
  | { name: "email"; meta: SecretMetaResponse; filename?: string; size?: number; emailError?: string }
  | { name: "otp"; meta: SecretMetaResponse; email: string; cooldownRemaining: number; attemptsError?: string }
  | { name: "decrypting" }
  | { name: "success_text"; text: string; oneTime: boolean; email: string; verifiedAt: number; canReply: boolean }
  | { name: "success_file"; filename: string; oneTime: boolean; email: string; verifiedAt: number; canReply: boolean }
  | { name: "fatal_error"; message: string };

/** What sits under the blur before "Click to reveal": the same shape for every
 * secret, so it gives away neither the text nor its length. */
const REVEAL_MASK = "•••••••• •••• ••••••••••• ••• ••••••\n••••• ••••••••• •••• ••••••• ••\n•••••••••• ••• ••••••";

export function RetrieveSecretPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { m, t, tp, tx, locale, formatBytes, formatUtcTime } = useI18n();
  const [stage, setStage] = useState<Stage>({ name: "loading" });
  const [emailInput, setEmailInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const passphraseRef = useRef<string | null>(null);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // The fatal-error stage holds a message rather than a code, so it is built
  // in whatever language was active when the failure happened. A language
  // switched afterwards won't retranslate it — accepted: these are terminal
  // screens, and the alternative is threading a code through every branch.
  useEffect(() => {
    // Captured out of the URL in main.tsx, before this page ever rendered.
    passphraseRef.current = linkKey();
    if (!passphraseRef.current) {
      reportBrowserEvent({ event: "link_opened", outcome: "missing_key" });
      setStage({ name: "invalid_link" });
      return;
    }
    reportBrowserEvent({ event: "link_opened", outcome: "ok" });

    getSecretMeta(id)
      .then(async (meta) => {
        if (!meta.exists) {
          reportBrowserEvent({ event: "meta_loaded", outcome: "not_found" });
          setStage({ name: "not_found" });
          return;
        }

        if (meta.type === "file" && meta.encryptedMeta) {
          try {
            const fileMeta = await decryptMeta(meta.encryptedMeta, passphraseRef.current!);
            reportBrowserEvent({ event: "meta_loaded", outcome: "ok", secretType: "file" });
            setStage({ name: "email", meta, filename: fileMeta.filename, size: fileMeta.size });
            return;
          } catch {
            // Falls through to a passphrase-less summary below — the fragment key doesn't match.
            reportBrowserEvent({ event: "meta_loaded", outcome: "key_mismatch", secretType: "file" });
            setStage({ name: "email", meta });
            return;
          }
        }

        reportBrowserEvent({ event: "meta_loaded", outcome: "ok", secretType: meta.type });
        setStage({ name: "email", meta });
      })
      .catch(() => {
        reportBrowserEvent({ event: "meta_loaded", outcome: "network_error" });
        setStage({ name: "fatal_error", message: m.retrieve.fatalError.cannotReachServer });
      });
    // Deliberately keyed on the secret only: re-running this on a language
    // change would re-request the metadata and throw away the stage the
    // reader is in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, []);

  function startCooldown(seconds: number, meta: SecretMetaResponse, email: string) {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    setStage({ name: "otp", meta, email, cooldownRemaining: seconds });
    cooldownTimer.current = setInterval(() => {
      setStage((prev) => {
        if (prev.name !== "otp") return prev;
        const next = prev.cooldownRemaining - 1;
        if (next <= 0 && cooldownTimer.current) {
          clearInterval(cooldownTimer.current);
        }
        return { ...prev, cooldownRemaining: Math.max(0, next) };
      });
    }, 1000);
  }

  const handleRequestOtp = useCallback(
    async (event: FormEvent, meta: SecretMetaResponse) => {
      event.preventDefault();
      setSubmitting(true);
      try {
        // `locale` goes up so the code email arrives in the language this
        // page is being read in. It is used for that one message and not
        // stored — see packages/shared/src/schemas.ts.
        const response = await requestOtp(id, { email: emailInput, locale });
        startCooldown(response.cooldownSeconds, meta, emailInput);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setStage((prev) => ({
            name: "email",
            meta,
            filename: prev.name === "email" ? prev.filename : undefined,
            size: prev.name === "email" ? prev.size : undefined,
            emailError: m.retrieve.email.restricted,
          }));
        } else if (err instanceof ApiError && err.status === 404) {
          setStage({ name: "not_found" });
        } else {
          setStage({ name: "fatal_error", message: m.retrieve.fatalError.cannotRequestCode });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [id, emailInput, locale, m],
  );

  async function handleVerifyOtp(event: FormEvent, current: Extract<Stage, { name: "otp" }>) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const { retrievalToken, replyGrant } = await verifyOtp(id, { email: current.email, code: codeInput });
      // Stashed now rather than at the end: the reply composer lives on its
      // own route, and this is the last moment the grant is in hand. Absent
      // on every deployment that hasn't turned replies on, in which case
      // "Send one back" stays the plain link to the create page it was.
      if (replyGrant) {
        storeReplyGrant({ grant: replyGrant, recipientEmail: current.email });
      }
      setStage({ name: "decrypting" });
      await retrieveAndDecrypt(retrievalToken, current, Boolean(replyGrant));
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.body?.error === "otp_incorrect") {
        const attemptsRemaining = (err.body as { attemptsRemaining?: number }).attemptsRemaining;
        setStage({
          ...current,
          attemptsError:
            attemptsRemaining != null
              ? tp(m.retrieve.otp.incorrectWithAttempts, attemptsRemaining)
              : m.retrieve.otp.incorrect,
        });
      } else if (err instanceof ApiError && err.body?.error === "otp_locked") {
        setStage({
          name: "email",
          meta: current.meta,
          emailError: m.retrieve.email.locked,
        });
      } else {
        setStage({ name: "fatal_error", message: m.retrieve.fatalError.cannotVerifyCode });
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function retrieveAndDecrypt(
    retrievalToken: string,
    current: Extract<Stage, { name: "otp" }>,
    canReply: boolean,
  ) {
    const passphrase = passphraseRef.current;
    if (!passphrase) {
      setStage({ name: "invalid_link" });
      return;
    }

    const { meta, email } = current;
    const oneTime = meta.oneTime ?? false;
    const secretType = meta.type === "file" ? "file" : "text";
    const startedAt = performance.now();
    const elapsed = () => Math.round(performance.now() - startedAt);

    try {
      const response = await downloadSecret(id, retrievalToken);

      if (meta.type === "text") {
        const body = (await response.json()) as { ciphertext: string };
        const plaintext = await decryptText(body.ciphertext, passphrase);
        reportBrowserEvent({ event: "secret_retrieved", outcome: "ok", secretType, durationMs: elapsed() });
        setRevealed(false);
        setStage({ name: "success_text", text: plaintext, oneTime, email, verifiedAt: Date.now(), canReply });
        return;
      }

      const decryptedStream = await decryptFileStream(response.body as ReadableStream<Uint8Array>, passphrase);
      const blob = await new Response(decryptedStream).blob();
      const fileMeta = meta.encryptedMeta ? await decryptMeta(meta.encryptedMeta, passphrase) : undefined;
      // Not translated: the fallback names a file the reader is about to
      // save, and a localised filename would land on disk in whatever
      // language the page happened to be in.
      const filename = fileMeta?.filename ?? "secret-file";

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      reportBrowserEvent({ event: "secret_retrieved", outcome: "ok", secretType, durationMs: elapsed() });
      setStage({ name: "success_file", filename, oneTime, email, verifiedAt: Date.now(), canReply });
    } catch (err) {
      // A one-time secret someone else already has in flight. Not a failure
      // of this retrieval so much as news about the secret, and worth its own
      // screen: "decryption failed" would send the recipient hunting for a
      // broken link that isn't broken.
      if (err instanceof ApiError && err.status === 410) {
        reportBrowserEvent({ event: "secret_retrieved", outcome: "not_found", secretType, durationMs: elapsed() });
        setStage({ name: "already_retrieved" });
        return;
      }

      // The two failures worth telling apart: the bytes never arrived, or
      // they arrived and wouldn't decrypt. Nothing about the error itself is
      // reported — an error message is exactly the kind of free text that
      // could carry a filename or a URL.
      reportBrowserEvent({
        event: "secret_retrieved",
        outcome: err instanceof ApiError ? "network_error" : "decrypt_failed",
        secretType,
        durationMs: elapsed(),
      });
      setStage({ name: "fatal_error", message: m.retrieve.fatalError.decryptionFailed });
    }
  }

  switch (stage.name) {
    case "loading":
      return (
        <Card narrow animate>
          <p className="muted">{m.common.loading}</p>
        </Card>
      );

    case "invalid_link":
      return (
        <Card narrow animate>
          <div className="heading-block">
            <h1>{m.retrieve.invalidLink.title}</h1>
            <p>{tx(m.retrieve.invalidLink.body, { hash: <code>#</code> })}</p>
            <p className="muted">{m.retrieve.invalidLink.note}</p>
          </div>
        </Card>
      );

    case "not_found":
      return (
        <Card narrow animate>
          <div className="heading-block">
            <h1>{m.retrieve.notFound.title}</h1>
            <p>{m.retrieve.notFound.body}</p>
          </div>
        </Card>
      );

    case "already_retrieved":
      return (
        <Card narrow animate>
          <div className="heading-block">
            <h1>{m.retrieve.alreadyRetrieved.title}</h1>
            <p>{m.retrieve.alreadyRetrieved.body}</p>
            <p className="muted">{m.retrieve.alreadyRetrieved.note}</p>
          </div>
        </Card>
      );

    case "fatal_error":
      return (
        <Card narrow animate>
          <div className="heading-block">
            <h1>{m.retrieve.fatalError.title}</h1>
          </div>
          <p className="error-text">{stage.message}</p>
        </Card>
      );

    case "email":
      return (
        <Card as="form" narrow animate onSubmit={(e: FormEvent) => handleRequestOtp(e, stage.meta)}>
          <div className="heading-block">
            <span className="eyebrow">{m.retrieve.email.eyebrow}</span>
            <h1>{m.retrieve.email.title}</h1>
            <p>
              {stage.filename &&
                t(m.retrieve.email.fileLine, {
                  filename: stage.filename,
                  size: stage.size == null ? "" : formatBytes(stage.size),
                })}
              {m.retrieve.email.body}
            </p>
          </div>

          <div className="unlock-fields">
            <label className="field-label" htmlFor="retrieve-email">
              {m.retrieve.email.label}
            </label>
            <input
              id="retrieve-email"
              className="email-input"
              type="email"
              required
              placeholder={m.retrieve.email.placeholder}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
            />
          </div>

          {stage.emailError && <p className="error-text">{stage.emailError}</p>}

          <div className="submit-block">
            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? m.retrieve.email.submitting : m.retrieve.email.submit}
            </button>
          </div>
        </Card>
      );

    case "otp":
      return (
        <Card as="form" narrow animate onSubmit={(e: FormEvent) => handleVerifyOtp(e, stage)}>
          <div className="heading-block">
            <span className="eyebrow">{m.retrieve.email.eyebrow}</span>
            <h1>{m.retrieve.otp.title}</h1>
            <p>{t(m.retrieve.otp.body, { email: stage.email })}</p>
          </div>

          <div className="unlock-fields">
            <label className="field-label" htmlFor="otp-code">
              {m.retrieve.otp.label}
            </label>
            <input
              id="otp-code"
              className="otp-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              placeholder="000000"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
            />
          </div>

          {stage.attemptsError && <p className="error-text">{stage.attemptsError}</p>}

          <div className="submit-block">
            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? m.retrieve.otp.submitting : m.retrieve.otp.submit}
            </button>
            {stage.meta.oneTime && <p className="submit-note">{m.retrieve.otp.oneTimeNote}</p>}
            <div className="resend-row">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={stage.cooldownRemaining > 0}
                onClick={(e) => handleRequestOtp(e, stage.meta)}
              >
                {stage.cooldownRemaining > 0
                  ? t(m.retrieve.otp.resendIn, { seconds: stage.cooldownRemaining })
                  : m.retrieve.otp.resend}
              </button>
            </div>
          </div>
        </Card>
      );

    case "decrypting":
      return (
        <Card narrow animate>
          <p className="muted">{m.retrieve.decrypting}</p>
        </Card>
      );

    case "success_text":
      return (
        <Card animate>
          <div className="reveal-title-row">
            <h1>{m.retrieve.success.textTitle}</h1>
            {stage.oneTime && <span className="reveal-status">{m.retrieve.success.destroyedOnServer}</span>}
          </div>

          <div className="reveal-box">
            {/* Until the click, the plaintext is not in the DOM at all — a blur
                alone leaves it selectable, findable and read out by a screen reader. */}
            {revealed ? (
              <pre className="reveal-pre" aria-live="polite">
                {stage.text}
              </pre>
            ) : (
              <pre className="reveal-pre reveal-pre--hidden" aria-hidden="true">
                {REVEAL_MASK}
              </pre>
            )}
            {!revealed && (
              <button type="button" className="reveal-overlay" onClick={() => setRevealed(true)}>
                <span className="reveal-pill">{m.retrieve.success.clickToReveal}</span>
              </button>
            )}
          </div>

          <div className="action-row">
            <CopyButton getText={() => stage.text} className="btn-dark-block" />
            <Link to={stage.canReply ? "/reply" : "/"} className="btn btn-secondary" style={{ textAlign: "center" }}>
              {m.retrieve.success.sendOneBack}
            </Link>
          </div>

          {stage.oneTime && (
            <div className="destroyed-notice">
              <span className="destroyed-dot" />
              <p className="destroyed-text">
                {t(m.retrieve.success.destroyedNotice, {
                  email: stage.email,
                  time: formatUtcTime(stage.verifiedAt),
                })}
              </p>
            </div>
          )}
        </Card>
      );

    case "success_file":
      return (
        <Card animate>
          <div className="reveal-title-row">
            <h1>{m.retrieve.success.fileTitle}</h1>
            {stage.oneTime && <span className="reveal-status">{m.retrieve.success.destroyedOnServer}</span>}
          </div>

          <p className="success-text">{t(m.retrieve.success.fileDownloaded, { filename: stage.filename })}</p>

          <div className="action-row">
            <Link to={stage.canReply ? "/reply" : "/"} className="btn btn-secondary" style={{ textAlign: "center" }}>
              {m.retrieve.success.sendOneBack}
            </Link>
          </div>

          {stage.oneTime && (
            <div className="destroyed-notice">
              <span className="destroyed-dot" />
              <p className="destroyed-text">
                {t(m.retrieve.success.destroyedNotice, {
                  email: stage.email,
                  time: formatUtcTime(stage.verifiedAt),
                })}
              </p>
            </div>
          )}
        </Card>
      );
  }
}
